import {
  Box,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppSelect from "@/features/line-sheet-sets/app-select";
import PriceAdjustedByTypeSelect from "@/features/order-sheet-sets/price-adjusted-by-type-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useListLineSheetSetProducts, {
  LineSheetSetProduct,
} from "@/features/line-sheet-sets/hooks/use-list-line-sheet-set-products";
import useResolveProductConditions from "@/features/line-sheet-sets/hooks/use-resolve-product-conditions";
import useGetInspectParsingJob from "@/features/line-sheet-sets/hooks/use-get-inspect-parsing-job";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { PriceAdjustedBy } from "@/features/line-sheet-sets/line-sheet-set.types";
import SVG from "react-inlinesvg";
import ProductConditionTable, {
  ProductConditionRow as TableRow,
  ValueSortDir,
} from "@/features/line-sheet-sets/product-condition-table";
import {
  ParseOrderRulesToConditionsBrandCondition,
  formatMatchedConditionLabel,
} from "@/features/line-sheet-sets/hooks/use-parse-order-rules-to-conditions";

type ConditionBy = PriceAdjustedBy["by"];

const ALL_VALUE = "__ALL__";

// 매칭된 오더룰(브랜드 조건 목록)을 자동 반영 API로 보낼 텍스트로 변환한다.
function formatMatchedConditionsAsText(
  conditions: ParseOrderRulesToConditionsBrandCondition[]
): string {
  return conditions
    .map((c) => `- ${formatMatchedConditionLabel(c)}`)
    .join("\n");
}

export interface ProductConditionEntry {
  productId: number;
  by: "MARKUP" | "DISCOUNT";
  value: string;
}

type ProductConditionRow = TableRow;

interface ProductConditionPanelProps {
  lineSheetSetId: number;
  // SEASON 타겟의 경우 id는 사용하지 않고 seasonName으로 시즌 문자열 전달.
  target?:
    | { kind: "BRAND"; id: number }
    | { kind: "LINE_SHEET"; id: number }
    | { kind: "SEASON"; id: number; seasonName: string }
    // 라인 시트 안의 특정 브랜드로 스코프 — brandId + lineSheetId 둘 다 사용.
    | { kind: "LINE_SHEET_BRAND"; lineSheetId: number; brandId: number }
    // 시즌 안의 특정 브랜드로 스코프 — brandId + season 둘 다 사용.
    | { kind: "SEASON_BRAND"; seasonName: string; brandId: number };
  initialEntries?: Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>;
  onEntriesChange?: (entries: ProductConditionEntry[]) => void;
  resetKey?: number; // bump to reset internal selection/filter state
  orderRulesText?: string;
  orderRuleFiles?: { id: number; name: string }[];
  matchedConditions?: ParseOrderRulesToConditionsBrandCondition[];
}

export default function ProductConditionPanel({
  lineSheetSetId,
  target,
  initialEntries,
  onEntriesChange,
  resetKey,
  orderRulesText,
  orderRuleFiles,
  matchedConditions,
}: ProductConditionPanelProps) {
  const { tTitle } = useI18nHelper();
  const { error: showError, info: showInfo } = useAppToasts();
  const { fire: fireResolveProductConditions } = useResolveProductConditions();
  const { fire: fireGetInspectParsingJob } = useGetInspectParsingJob();
  const [isResolvingConditions, setIsResolvingConditions] = useState(false);
  const orderRuleFileList = orderRuleFiles ?? [];
  const matchedConditionList = matchedConditions ?? [];
  // 자동 반영 전, 보낼 오더룰을 편집/선택하는 모달 상태.
  // 매칭된 오더룰 / 텍스트 편집 / 파일 1개 선택 중 하나만 사용한다.
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [sourceMode, setSourceMode] = useState<"MATCHED" | "TEXT" | "FILE">(
    "TEXT"
  );
  const [editableText, setEditableText] = useState<string>("");
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);

  const fetchParameter = { lineSheetSetId };
  const fetchRequest = target
    ? target.kind === "BRAND"
      ? { brandId: target.id }
      : target.kind === "LINE_SHEET"
      ? { lineSheetId: target.id }
      : target.kind === "LINE_SHEET_BRAND"
      ? { brandId: target.brandId, lineSheetId: target.lineSheetId }
      : target.kind === "SEASON_BRAND"
      ? { brandId: target.brandId, season: target.seasonName }
      : { season: target.seasonName }
    : undefined;
  const { data: rawData, isLoading } = useListLineSheetSetProducts(
    fetchParameter,
    fetchRequest
  );

  const products = useMemo<LineSheetSetProduct[]>(() => {
    if (!rawData) return [];
    const list: any[] = (() => {
      if (Array.isArray(rawData)) return rawData;
      const anyData = rawData as any;
      if (Array.isArray(anyData.products)) return anyData.products;
      if (Array.isArray(anyData.items)) return anyData.items;
      if (Array.isArray(anyData.data)) return anyData.data;
      return [];
    })();
    return list.map((p: any) => {
      const pab = p.priceAdjustedBy ?? p.price_adjusted_by ?? null;
      return {
        id: p.id,
        sku: p.sku ?? "",
        brandId: p.brandId ?? p.brand?.id ?? p.brand_id ?? null,
        brandName: p.brandName ?? p.brand?.name ?? p.brand_name ?? "",
        productName: p.productName ?? p.product_name ?? p.name ?? "",
        gender: p.gender ?? null,
        category: p.category ?? null,
        subCategory: p.subCategory ?? p.sub_category ?? null,
        season: p.season ?? null,
        note: p.note ?? null,
        images: Array.isArray(p.images) ? p.images : [],
        priceAdjustedBy: pab
          ? {
              by: pab.by,
              value: pab.value,
              applyIva: pab.applyIva ?? pab.apply_iva ?? null,
            }
          : null,
      };
    }) as LineSheetSetProduct[];
  }, [rawData]);

  const [brandFilter, setBrandFilter] = useState<string>(ALL_VALUE);
  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_VALUE);
  const [genderFilter, setGenderFilter] = useState<string>(ALL_VALUE);
  const [rows, setRows] = useState<ProductConditionRow[]>([]);
  const [bulkBy, setBulkBy] = useState<ConditionBy>("MARKUP");
  const [bulkValue, setBulkValue] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [valueSortDir, setValueSortDir] = useState<ValueSortDir>("none");

  useEffect(() => {
    setBrandFilter(ALL_VALUE);
    setCategoryFilter(ALL_VALUE);
    setGenderFilter(ALL_VALUE);
    setBulkBy("MARKUP");
    setBulkValue("");
    setSelectedIds(new Set());
    setValueSortDir("none");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    setRows(
      products.map((p) => {
        const existing = initialEntries?.get(p.id);
        const fallbackBy = p.priceAdjustedBy?.by;
        const fallbackValue =
          p.priceAdjustedBy?.value !== undefined &&
          p.priceAdjustedBy?.value !== null
            ? String(p.priceAdjustedBy.value)
            : "";
        return {
          productId: p.id,
          product: p,
          by: (existing?.by ?? fallbackBy ?? "MARKUP") as ConditionBy,
          value: existing?.value ?? fallbackValue,
        };
      })
    );
  }, [products, initialEntries]);

  useEffect(() => {
    if (!onEntriesChange) return;
    onEntriesChange(
      rows.map((r) => ({
        productId: r.productId,
        by: (r.by === "DISCOUNT" ? "DISCOUNT" : "MARKUP") as
          | "MARKUP"
          | "DISCOUNT",
        value: r.value,
      }))
    );
  }, [rows, onEntriesChange]);

  const brandOptions = useMemo(() => {
    const seen = new Set<string>();
    products.forEach((p) => {
      if (!p.brandName) return;
      seen.add(p.brandName);
    });
    return [
      { name: tTitle("all"), value: ALL_VALUE },
      ...Array.from(seen)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ name, value: name })),
    ];
  }, [products, tTitle]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    products.forEach((p) => {
      if (p.category) seen.add(p.category);
    });
    return [
      { name: tTitle("all"), value: ALL_VALUE },
      ...Array.from(seen).map((c) => ({ name: c, value: c })),
    ];
  }, [products, tTitle]);

  const genderOptions = useMemo(() => {
    const seen = new Set<string>();
    products.forEach((p) => {
      if (p.gender) seen.add(p.gender);
    });
    return [
      { name: tTitle("all"), value: ALL_VALUE },
      ...Array.from(seen).map((g) => ({ name: g, value: g })),
    ];
  }, [products, tTitle]);

  const filteredIndexes = useMemo(() => {
    const indexes: number[] = [];
    rows.forEach((row, idx) => {
      if (
        brandFilter !== ALL_VALUE &&
        (row.product.brandName ?? "") !== brandFilter
      )
        return;
      if (
        categoryFilter !== ALL_VALUE &&
        (row.product.category ?? "") !== categoryFilter
      )
        return;
      if (
        genderFilter !== ALL_VALUE &&
        (row.product.gender ?? "") !== genderFilter
      )
        return;
      indexes.push(idx);
    });
    return indexes;
  }, [rows, brandFilter, categoryFilter, genderFilter]);

  // 값 정렬: 필터된 행을 value(숫자) 기준으로 정렬. 빈 값/숫자 아님은 항상 뒤로.
  const displayIndexes = useMemo(() => {
    if (valueSortDir === "none") return filteredIndexes;
    const parseValue = (v: string): number | null => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    return [...filteredIndexes].sort((a, b) => {
      const va = parseValue(rows[a].value);
      const vb = parseValue(rows[b].value);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return valueSortDir === "asc" ? va - vb : vb - va;
    });
  }, [filteredIndexes, rows, valueSortDir]);

  const handleToggleValueSort = () => {
    setValueSortDir((prev) =>
      prev === "none" ? "asc" : prev === "asc" ? "desc" : "none"
    );
  };

  const handleChangeRow = (
    index: number,
    patch: Partial<ProductConditionRow>
  ) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const handleApplyToSelected = () => {
    setRows((prev) =>
      prev.map((r) =>
        selectedIds.has(r.productId)
          ? { ...r, by: bulkBy, value: bulkValue }
          : r
      )
    );
  };

  const toggleRowSelection = (productId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const filteredProductIds = useMemo(
    () => filteredIndexes.map((i) => rows[i].productId),
    [filteredIndexes, rows]
  );

  // 필터가 바뀌면 현재 필터에 포함되지 않은 선택은 해제한다.
  // (전체선택 후 필터를 걸면 화면에 안 보이는 상품이 선택된 채 남아
  //  일괄 적용이 필터 밖 상품까지 건드리는 문제를 막는다.)
  useEffect(() => {
    const filteredSet = new Set(filteredProductIds);
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<number>();
      prev.forEach((id) => {
        if (filteredSet.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [filteredProductIds]);

  const filteredSelectedCount = useMemo(
    () => filteredProductIds.filter((id) => selectedIds.has(id)).length,
    [filteredProductIds, selectedIds]
  );
  const isAllFilteredSelected =
    filteredProductIds.length > 0 &&
    filteredSelectedCount === filteredProductIds.length;
  const isSomeFilteredSelected =
    filteredSelectedCount > 0 &&
    filteredSelectedCount < filteredProductIds.length;

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllFilteredSelected) {
        filteredProductIds.forEach((id) => next.delete(id));
      } else {
        filteredProductIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // 버튼 클릭 핸들러: 바로 API를 호출하지 않고, 보낼 오더룰을 편집/선택하는
  // 모달을 먼저 띄운다. 텍스트 편집 또는 파일 1개 선택 중 하나만 보낸다.
  const handleAutoApplyFromOrderRules = () => {
    if (isResolvingConditions) return;
    setEditableText(orderRulesText ?? "");
    setSelectedFileId(orderRuleFileList[0]?.id ?? null);
    // 기본 모드: 매칭된 오더룰이 있으면 그것, 없으면 텍스트(없고 파일만 있으면 파일).
    setSourceMode(
      matchedConditionList.length > 0
        ? "MATCHED"
        : (orderRulesText?.trim() ?? "") === "" && orderRuleFileList.length > 0
        ? "FILE"
        : "TEXT"
    );
    setIsSourceModalOpen(true);
  };

  const runAutoApply = async (source: {
    orderRulesText?: string;
    unoFileId?: number;
  }) => {
    if (isResolvingConditions) return;
    setIsResolvingConditions(true);
    try {
      const targetBody = target
        ? target.kind === "BRAND"
          ? { brandId: target.id }
          : target.kind === "LINE_SHEET"
          ? { lineSheetId: target.id }
          : target.kind === "LINE_SHEET_BRAND"
          ? { brandId: target.brandId, lineSheetId: target.lineSheetId }
          : target.kind === "SEASON_BRAND"
          ? { brandId: target.brandId, season: target.seasonName }
          : { season: target.seasonName }
        : {};
      const requestBody = {
        ...targetBody,
        ...(source.orderRulesText !== undefined
          ? { orderRulesText: source.orderRulesText }
          : {}),
        ...(source.unoFileId !== undefined
          ? { unoFileId: source.unoFileId }
          : {}),
      };
      const startRes = await fireResolveProductConditions(
        { lineSheetSetId },
        requestBody
      );
      if (!startRes || !startRes.jobId) {
        showError(new Error("작업을 시작하지 못했습니다."));
        return;
      }
      const POLL_INTERVAL_MS = 2000;
      const MAX_POLL_ATTEMPTS = 60;
      let finalConditions:
        | {
            [productId: string]: {
              by: "MARKUP" | "DISCOUNT";
              value: number;
              applyIva: boolean | null;
            };
          }
        | undefined;
      for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
        await new Promise((resolve) =>
          setTimeout(resolve, POLL_INTERVAL_MS)
        );
        const statusRes = await fireGetInspectParsingJob(
          { jobId: startRes.jobId },
          {}
        );
        const status = (statusRes?.status ?? "").toLowerCase();
        if (status === "completed" || status === "succeeded") {
          const result = statusRes?.result as any;
          if (result?.conditions) {
            finalConditions = result.conditions;
          } else if (result && typeof result === "object") {
            finalConditions = result;
          }
          break;
        }
        if (status === "failed" || status === "error") {
          showError(
            new Error(
              statusRes?.error || "오더룰 자동 반영에 실패했습니다."
            )
          );
          return;
        }
      }
      if (!finalConditions) {
        showError(new Error("오더룰 자동 반영이 시간 내에 완료되지 않았습니다."));
        return;
      }
      let appliedCount = 0;
      const nextRows = rows.map((row) => {
        const cond = finalConditions![String(row.productId)];
        if (!cond) return row;
        appliedCount++;
        return {
          ...row,
          by: cond.by,
          value: String(cond.value),
        };
      });
      setRows(nextRows);
      showInfo(`${appliedCount}개 상품 자동 반영`);
    } catch (e) {
      showError(e);
    } finally {
      setIsResolvingConditions(false);
    }
  };

  return (
    <>
    <Flex flexDirection={"column"} gap={"12px"}>
      <Flex justifyContent={"flex-end"}>
        <AppButton
          variant={"normal"}
          chakraProps={{ p: "4px 10px", flexShrink: 0 }}
          isLoading={isResolvingConditions}
          onClick={handleAutoApplyFromOrderRules}
        >
          <Text fontSize={"11px"} fontWeight={"700"}>
            {tTitle("auto_apply_from_order_rules")}
          </Text>
        </AppButton>
      </Flex>

      {/* 필터 */}
      <Flex
        alignItems={"center"}
        gap={"10px"}
        padding={"10px 12px"}
        borderRadius={"6px"}
        border={"0.5px solid #D4D4D1"}
        background={"#FCFCFE"}
      >
        <Box width={"1px"} height={"20px"} background={"#E5E5E5"} />
        <Flex alignItems={"center"} gap={"6px"} flexShrink={0}>
          <Text fontSize={"11px"} color={"#6F6F6C"}>
            {tTitle("brand")}
          </Text>
          <Box width={"170px"}>
            <AppSelect
              name={tTitle("brand")}
              options={brandOptions}
              value={brandFilter}
              isClearable={false}
              onSelect={(v) => v && setBrandFilter(v as string)}
              width={"170px"}
            />
          </Box>
        </Flex>
        <Flex alignItems={"center"} gap={"6px"} flexShrink={0}>
          <Text fontSize={"11px"} color={"#6F6F6C"}>
            {tTitle("category")}
          </Text>
          <Box width={"150px"}>
            <AppSelect
              name={tTitle("category")}
              options={categoryOptions}
              value={categoryFilter}
              isClearable={false}
              onSelect={(v) => v && setCategoryFilter(v as string)}
              width={"150px"}
            />
          </Box>
        </Flex>
        <Flex alignItems={"center"} gap={"6px"} flexShrink={0}>
          <Text fontSize={"11px"} color={"#6F6F6C"}>
            {tTitle("gender")}
          </Text>
          <Box width={"120px"}>
            <AppSelect
              name={tTitle("gender")}
              options={genderOptions}
              value={genderFilter}
              isClearable={false}
              onSelect={(v) => v && setGenderFilter(v as string)}
              width={"120px"}
            />
          </Box>
        </Flex>
        <Box flex={1} />
        <AppButton
          variant={"normal"}
          chakraProps={{ p: "4px 10px", flexShrink: 0 }}
          isDisabled={
            brandFilter === ALL_VALUE &&
            categoryFilter === ALL_VALUE &&
            genderFilter === ALL_VALUE
          }
          onClick={() => {
            setBrandFilter(ALL_VALUE);
            setCategoryFilter(ALL_VALUE);
            setGenderFilter(ALL_VALUE);
          }}
        >
          <Text fontSize={"11px"}>{tTitle("reset_filter")}</Text>
        </AppButton>
      </Flex>

      {/* 일괄 적용 */}
      <Flex
        alignItems={"center"}
        gap={"8px"}
        padding={"8px 12px"}
        background={"#F7F9FA"}
        borderRadius={"6px"}
        border={"0.5px solid #D4D4D1"}
      >
        <Text fontSize={"12px"} fontWeight={"700"} width={"100px"}>
          {tTitle("all")}
        </Text>
        <Box width={"120px"}>
          <PriceAdjustedByTypeSelect
            width={"120px"}
            value={bulkBy}
            onSelect={(v) => {
              if (v) setBulkBy(v);
            }}
          />
        </Box>
        <Input
          width={"80px"}
          type={"number"}
          fontSize={"12px"}
          height={"28px"}
          padding={"0 8px"}
          borderColor={"#D4D4D1"}
          background={"#FFFFFF"}
          _hover={{ borderColor: "#D4D4D1" }}
          _focus={{ borderColor: "#8F8F8C", boxShadow: "none" }}
          value={bulkValue}
          onChange={(e) => setBulkValue(e.target.value)}
        />
        <AppButton
          variant={"normal"}
          onClick={handleApplyToSelected}
          isDisabled={selectedIds.size === 0}
        >
          <Text fontSize={"12px"}>
            {tTitle("apply")} ({selectedIds.size})
          </Text>
        </AppButton>
      </Flex>

      {/* 테이블 */}
      <ProductConditionTable
        rows={rows}
        filteredIndexes={displayIndexes}
        selectedIds={selectedIds}
        isLoading={isLoading}
        isAllFilteredSelected={isAllFilteredSelected}
        isSomeFilteredSelected={isSomeFilteredSelected}
        hasFiltered={filteredProductIds.length > 0}
        valueSortDir={valueSortDir}
        onToggleValueSort={handleToggleValueSort}
        onToggleSelectAll={toggleSelectAllFiltered}
        onToggleRow={toggleRowSelection}
        onChangeRow={handleChangeRow}
      />
    </Flex>

    {/* 자동 반영 전, 보낼 오더룰을 편집/선택: 텍스트 편집 또는 파일 1개 선택 중 하나만 */}
    <Modal
      isOpen={isSourceModalOpen}
      onClose={() => setIsSourceModalOpen(false)}
      size={"lg"}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          color={"#444440"}
          fontSize={"14px"}
          background={"#F7F9FA"}
          borderBottom={"0.5px solid #8F8F8C"}
        >
          {tTitle("auto_apply_from_order_rules")}
        </ModalHeader>
        <ModalBody>
          <Text fontSize={"12px"} color={"#6F6F6C"} marginBottom={"12px"}>
            {tTitle("order_rule_source_description")}
          </Text>
          <RadioGroup
            value={sourceMode}
            onChange={(v) =>
              setSourceMode(v as "MATCHED" | "TEXT" | "FILE")
            }
          >
            <Flex gap={"16px"} marginBottom={"12px"}>
              {matchedConditionList.length > 0 && (
                <Radio value={"MATCHED"}>
                  <Text fontSize={"12px"} fontWeight={"700"}>
                    {tTitle("matched_order_rule")}
                  </Text>
                </Radio>
              )}
              <Radio value={"TEXT"}>
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {tTitle("order_rule_text")}
                </Text>
              </Radio>
              <Radio
                value={"FILE"}
                isDisabled={orderRuleFileList.length === 0}
              >
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {tTitle("order_rule_file")}
                </Text>
              </Radio>
            </Flex>
          </RadioGroup>

          {sourceMode === "MATCHED" ? (
            <Flex
              flexWrap={"wrap"}
              gap={"4px"}
              padding={"10px"}
              borderRadius={"6px"}
              border={"0.5px solid #E5DCA0"}
              background={"#FFF7CC"}
              minH={"60px"}
              alignContent={"flex-start"}
            >
              {matchedConditionList.map((c, ci) => (
                <Text
                  key={ci}
                  fontSize={"11px"}
                  color={"#7A6A00"}
                  background={"#FCFCFE"}
                  border={"0.5px solid #E5DCA0"}
                  borderRadius={"3px"}
                  padding={"2px 8px"}
                  whiteSpace={"nowrap"}
                >
                  {formatMatchedConditionLabel(c)}
                </Text>
              ))}
            </Flex>
          ) : sourceMode === "TEXT" ? (
            <Textarea
              value={editableText}
              onChange={(e) => setEditableText(e.target.value)}
              placeholder={tTitle("order_rule_text_placeholder")}
              fontSize={"12px"}
              minH={"220px"}
              borderColor={"#D4D4D1"}
              _hover={{ borderColor: "#D4D4D1" }}
              _focus={{ borderColor: "#8F8F8C", boxShadow: "none" }}
            />
          ) : (
            <RadioGroup
              value={selectedFileId !== null ? String(selectedFileId) : ""}
              onChange={(v) => setSelectedFileId(Number(v))}
            >
              <Flex flexDirection={"column"} gap={"8px"}>
                {orderRuleFileList.map((file) => (
                  <Box
                    key={file.id}
                    padding={"8px 10px"}
                    borderRadius={"6px"}
                    border={"0.5px solid #D4D4D1"}
                    background={
                      selectedFileId === file.id ? "#F0F6FF" : "#FCFCFE"
                    }
                    cursor={"pointer"}
                    onClick={() => setSelectedFileId(file.id)}
                  >
                    <Radio value={String(file.id)} alignItems={"center"}>
                      <Text
                        fontSize={"12px"}
                        color={"#444444"}
                        overflow={"hidden"}
                        textOverflow={"ellipsis"}
                        whiteSpace={"nowrap"}
                      >
                        {file.name}
                      </Text>
                    </Radio>
                  </Box>
                ))}
              </Flex>
            </RadioGroup>
          )}
        </ModalBody>
        <ModalFooter display={"flex"} gap={"12px"}>
          <AppButton onClick={() => setIsSourceModalOpen(false)}>
            {tTitle("cancel")}
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={
              sourceMode === "MATCHED"
                ? matchedConditionList.length === 0
                : sourceMode === "TEXT"
                ? editableText.trim() === ""
                : selectedFileId === null
            }
            onClick={() => {
              if (sourceMode === "MATCHED") {
                if (matchedConditionList.length === 0) return;
                setIsSourceModalOpen(false);
                void runAutoApply({
                  orderRulesText:
                    formatMatchedConditionsAsText(matchedConditionList),
                });
              } else if (sourceMode === "TEXT") {
                if (editableText.trim() === "") return;
                setIsSourceModalOpen(false);
                void runAutoApply({ orderRulesText: editableText });
              } else {
                if (selectedFileId === null) return;
                setIsSourceModalOpen(false);
                void runAutoApply({ unoFileId: selectedFileId });
              }
            }}
          >
            {tTitle("apply")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
}
